"use client";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { WORKFLOW_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveWorkflow from "@/hooks/requests/wokflow/use-remove-workflow";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface WorkflowPopoverProps {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: WorkflowMessages;
  selectedWorkflow: IWorkflow;
}

export function WorkflowPopover({
  isOpen,
  anchorEl,
  onClose,
  messages,
  selectedWorkflow,
}: WorkflowPopoverProps) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<IWorkflow>();
  const { removeWorkflow, isLoading: isLoadingRemove } = useRemoveWorkflow();
  const { mutate } = useMutate();

  const hasNoPermissionToEdit = useHasNoPermission(WORKFLOW_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(WORKFLOW_PERMISSIONS.DELETE);

  const { updatePopover, viewPopover, removePopover } = messages;

  const handleOnEdit = () => {
    router(ROUTE_URLS.updateWorkflow(lang, selectedWorkflow.id));
  };

  const handleOnView = () => {
    router(ROUTE_URLS.flowWorkflow(lang, selectedWorkflow.id));
  };

  const handleConfirmDelete = async () => {
    const _id = selectedWorkflow.id;
    const successMessage = messages.deleteWorkflowSuccess;
    const errorMessage = messages.deleteWorkflowError;

    const onSuccess = () => {
      mutate(SWR_KEYS.getWorkflows);
      handleClose();
      onClose();
    };

    await removeWorkflow({ _id, onSuccess, successMessage, errorMessage });
  };

  const isDeleteDisabled = hasNoPermissionToDelete || selectedWorkflow.active;

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <NotificationItem icon={<VisibilityIcon />} onClick={handleOnView} title={viewPopover} />

      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={handleOnEdit}
        title={updatePopover}
      />

      <NotificationItem
        icon={<DeleteIcon />}
        disabled={isDeleteDisabled}
        onClick={() => handleOpen(selectedWorkflow)}
        title={removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={handleConfirmDelete}
        titleText={messages.deleteWorkflowDialogTitle}
        descriptionText={messages.deleteWorkflowDialogContent}
        cancelText={messages.deleteWorkflowDialogCancel}
        confirmText={messages.deleteWorkflowDialogConfirm}
        isLoading={isLoadingRemove}
      />
    </Menu>
  );
}
